import React, { useState, useContext, useCallback } from 'react';
import { BulkOrderContext } from './BulkOrderContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { CustomRadioSelector } from './CustomRadioSelector';
import { CustomTextInput } from './CustomTextInput';
import { Modal } from './Modal';
import { DatePicker } from 'components/date-picker';
import { formatDateObject } from 'common/utils/time';
import { Faceplate } from 'common/api/misc';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { ReviewOrderStep } from './ReviewOrderStep';
import { Product } from 'common/api/e-comm/models/Product';
import { useFormikContext } from 'formik';
import { ProductAmountInputFormData } from 'features/product/components/ProductAmountInput';
import type { ModalControls } from 'hooks/useModalControls';
import { Step4 } from './Step4';
import { styles } from './styles';
import { colors } from 'styles/settings';
import { Btn } from 'components/btn/Btn';

function addMonthsFromNow(months: number) {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date;
}

interface BulkOrderFormProps {
    onCancel: (location?: string) => void;
    brandName: string;
    amount: string;
    product: Product;
    recipientsListModal: ModalControls;
}

const getRebate = (
    priceOverride: number,
    selectedOptions: { [key: string]: string },
    product: Product
) => {
    let rebate = 0;
    if (priceOverride) {
        rebate = product.bonus?.rebate || product.rebate;
    } else {
        const selectedValues = Object.values(selectedOptions);

        if (selectedValues.length > 0) {
            const value = parseInt(selectedValues[0].substr(1) || '0.00');

            for (let i = 0; i < product.variants.length; i++) {
                const variant = product.variants[i];
                if (variant.price === value) {
                    rebate = variant.bonus?.rebate || variant.rebate;
                    break;
                }
            }
        }
    }

    return rebate / 100;
};

const BulkOrderForm = ({
    onCancel,
    brandName,
    amount,
    product,
    recipientsListModal,
}: BulkOrderFormProps) => {
    const [showModal, setShowModal] = useState(false);
    const [deliveryOption, setDeliveryOption] = useState<string>('Now');
    const today = new Date();
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

    const { state, setStep, setSelectedCardDesign, setDeliveryDate, setSenderName, setMessage } =
        useContext(BulkOrderContext);

    const handleInputChange = useCallback(
        debounce(
            (value: string, updateFn: (_: string) => void) => {
                updateFn(value);
            },
            100,
            { leading: true }
        ),
        []
    );

    const formikCtx = useFormikContext<ProductAmountInputFormData>();
    const rebate = getRebate(
        formikCtx.values.priceOverride || 0,
        formikCtx.values.selectedOptions,
        product
    );

    const step = state.step;

    const [senderNameHasValue, setSenderNameHasValue] = useState(false);

    switch (step) {
        case 1:
            return (
                <>
                    <div className={styles.stepContentWrapper}>
                        <h2 className={styles.formHeader}>Choose Your Design</h2>

                        <div className="grid grid-cols-3 gap-1">
                            {state.giftImageOptions &&
                                state.giftImageOptions.map((option: Faceplate, index: number) => (
                                    <button
                                        key={option.ImageUrl}
                                        onClick={() => {
                                            setSelectedCardDesign(option);
                                        }}
                                        className={classNames(
                                            'cursor-pointer p-1 focus:outline-none rounded-lg border-2 hover:border-brand-pale-blue',
                                            state.selectedCardDesign?.ImageId === option.ImageId
                                                ? 'border-brand-2'
                                                : 'border-white'
                                        )}
                                    >
                                        <img
                                            src={option.ImageUrl}
                                            alt={`Design ${index + 1}`}
                                            className="h-16 object-contain mx-auto"
                                        />
                                    </button>
                                ))}
                        </div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="mb-2">
                            <Btn
                                className={styles.stepperButton}
                                onClick={() => setStep(state.step + 1)}
                            >
                                Next
                                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </Btn>
                        </div>

                        <div>
                            <button
                                className={styles.stepperButtonClear}
                                onClick={() => onCancel()}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </>
            );
        case 2:
            return (
                <>
                    {showModal && (
                        <Modal onClose={() => setShowModal(false)}>
                            <h2 className={styles.formHeader}>Schedule Delivery</h2>

                            <div className="flex-grow"></div>

                            <div className="flex flex-col items-center my-6">
                                <DatePicker
                                    inline
                                    selected={state.deliveryDate}
                                    onChange={(dt) => setDeliveryDate(dt as Date)}
                                    minDate={tomorrow}
                                    maxDate={addMonthsFromNow(12)}
                                />

                                <div className="mb-2 mt-4">
                                    <button
                                        className={styles.stepperButton}
                                        onClick={() => setShowModal(false)}
                                    >
                                        Apply
                                    </button>
                                </div>

                                <div>
                                    <button
                                        className={styles.stepperButtonClear}
                                        onClick={() => {
                                            setDeliveryDate(today);
                                            setShowModal(false);
                                            setDeliveryOption('Now');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    )}

                    <div className={styles.stepContentWrapper}>
                        <h2 className={styles.formHeader}>Select a Delivery Date</h2>

                        <div className="flex justify-center">
                            <CustomRadioSelector
                                options={['Now', 'Schedule Delivery']}
                                value={deliveryOption}
                                onSelectionChange={(option) => {
                                    setDeliveryOption(option);

                                    if (option === 'Now') {
                                        setDeliveryDate(today);
                                    } else if (option === 'Schedule Delivery') {
                                        setShowModal(true);
                                        setDeliveryDate(tomorrow);
                                    }
                                }}
                            />
                            <div className={styles.dateMarginContainer}>
                                <div className={styles.dateMarginRow}>
                                    <div className={styles.dateMarginEntry}>
                                        {deliveryOption === 'Now' && !showModal ? (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    color={colors.alertGreen}
                                                />
                                                {state.deliveryDate && (
                                                    <span className="mx-2">
                                                        {formatDateObject(state?.deliveryDate)}
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            <span></span>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.dateMarginRow}>
                                    {deliveryOption === 'Schedule Delivery' && !showModal && (
                                        <div className={styles.dateMarginEntry}>
                                            <FontAwesomeIcon
                                                color={colors.alertGreen}
                                                icon={faCheck}
                                            />
                                            {state.deliveryDate && (
                                                <span className="mx-2">
                                                    {formatDateObject(state?.deliveryDate)}
                                                </span>
                                            )}

                                            <button
                                                className="text-brand text-xs underline "
                                                onClick={() => {
                                                    setShowModal(true);
                                                }}
                                            >
                                                Change
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-center">
                        <div className="mb-2">
                            <Btn className={styles.stepperButton} onClick={() => setStep(step + 1)}>
                                Next
                                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </Btn>
                        </div>

                        <div>
                            {state.giftImageOptions && state.giftImageOptions.length === 1 ? (
                                <button
                                    className={styles.stepperButtonClear}
                                    onClick={() => onCancel()}
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    className={styles.stepperButtonClear}
                                    onClick={() => setStep(step - 1)}
                                >
                                    Back
                                </button>
                            )}
                        </div>
                    </div>
                </>
            );
        case 3:
            return (
                <>
                    <div className={styles.stepContentWrapper}>
                        <div className="w-full">
                            <h2 className={styles.formHeader}>Make it Memorable</h2>

                            <p className="text-2xl text-grey-medium font-normal -mt-8">
                                Brighten their day with a custom message.
                            </p>
                        </div>
                        <div className=" flex flex-col items-center mt-12 mb-4">
                            <CustomTextInput
                                label="Enter Sender's Name"
                                name="sender"
                                onChange={({ target }) => {
                                    handleInputChange(target.value, setSenderName);
                                }}
                                onHasValueChange={setSenderNameHasValue}
                                value={state.senderName}
                                errorMessage="Sender's Name is required."
                            />

                            <br />

                            <CustomTextInput
                                label="Message (optional)"
                                textarea
                                name="senderMessage"
                                onChange={({ target }) =>
                                    handleInputChange(target.value, setMessage)
                                }
                                value={state.message}
                                showCounter
                            />
                        </div>
                    </div>

                    <div className="flex flex-col items-center">
                        <div className="mb-2">
                            <Btn
                                className={styles.stepperButton}
                                onClick={() => setStep(step + 1)}
                                disabled={!senderNameHasValue}
                            >
                                Next
                                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </Btn>
                        </div>
                        <div>
                            <button
                                className={styles.stepperButtonClear}
                                onClick={() => setStep(step - 1)}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </>
            );
        case 4:
            return <Step4 recipientsListModal={recipientsListModal} />;
        case 5:
            return (
                <ReviewOrderStep
                    amount={amount}
                    brandName={brandName}
                    goBack={() => setStep(step - 1)}
                    rebate={rebate}
                    showRecipientModal={recipientsListModal.toggleModalVisibility}
                />
            );
        default:
            return null;
    }
};

export default BulkOrderForm;
